/**
 * Job seeker profile completion for the dashboard widget (excludes references, certifications, work experience).
 * Aligns with Edit Profile fields plus Skills, Education, Languages.
 */

function filled(val) {
    return val != null && String(val).trim() !== '';
}

function hasCountry(profile) {
    if (!profile) return false;
    return !!(profile.country?.id ?? profile.country_id);
}

/**
 * @param {object|null} profile — from /auth/profile/me/
 * @param {{ skills?: array, educations?: array, languages?: array }} sections
 * @returns {{ percent: number, done: number, total: number, checks: { key: string, done: boolean }[] }}
 */
export function computeJobseekerProfileCompletion(profile, { skills = [], educations = [], languages = [] } = {}) {
    const checks = [
        { key: 'first_name', done: filled(profile?.first_name) },
        { key: 'last_name', done: filled(profile?.last_name) },
        { key: 'home_address', done: filled(profile?.home_address) },
        { key: 'postalcode', done: filled(profile?.postalcode) },
        { key: 'city', done: filled(profile?.city) },
        { key: 'country', done: hasCountry(profile) },
        { key: 'phone_number', done: filled(profile?.phone_number) },
        { key: 'about', done: filled(profile?.about) },
        { key: 'skills', done: skills.length > 0 },
        { key: 'education', done: educations.length > 0 },
        { key: 'languages', done: languages.length > 0 },
    ];
    const total = checks.length;
    const done = checks.filter((c) => c.done).length;
    const percent = total ? Math.round((done / total) * 100) : 0;
    return { percent, done, total, checks };
}

/**
 * Minimum bar for turning on employer-visible profile (mirrors backend `profile_meets_visibility_minimum`).
 */
export function meetsProfileVisibilityMinimum(profile, { skills = [], educations = [], languages = [] } = {}) {
    if (filled(profile?.about)) return true;
    if (filled(profile?.phone_number)) return true;
    if (filled(profile?.city)) return true;
    if (hasCountry(profile)) return true;
    if (skills.length > 0) return true;
    if (educations.length > 0) return true;
    if (languages.length > 0) return true;
    return false;
}
